import React from "react";
import { observer } from "mobx-react-lite";
import { Modal } from "antd";
import { NGLabel } from "../../newcomponents/text/label";
import { InfoTooltip } from "../InfoTooltip";
import { NGInput } from "../../newcomponents/input/input";
import { K8S_CRD_DEFAULT_NAMESPACE } from "./viewModal";
import { NGCheckbox } from "../../newcomponents/checkbox";
import { ConsoleContext } from "../../mobxStores/consoleContext/consoleContext";
import { itemHelpers as itemHelpersBase } from "../../mst/kinds/item.helpers";
import { Kind } from "../../schema/types/base";
import { NGButton } from "../../newcomponents/button/Button";

interface Props {
  kind: Kind;
  links: string[];
  onClose?: () => void;
}

const K8sCrdExportModalRaw: React.FC<Props> = ({ kind, links, onClose }) => {
  const itemHelpers = itemHelpersBase({ org: ConsoleContext.org!, gvc: ConsoleContext.gvc, kind: kind });

  // ANCHOR - States
  const [isOpen, setIsOpen] = React.useState<boolean>(true);
  const [k8sCrdNamespace, setK8sCrdNamespace] = React.useState<string>(K8S_CRD_DEFAULT_NAMESPACE);
  const [k8sCrdKeep, setK8sCrdKeep] = React.useState<boolean>(false);

  // ANCHOR - Functions
  async function onOk(): Promise<void> {
    // Hide modal
    setIsOpen(false);

    // Export to K8s CRD format
    await itemHelpers.handleExportK8sCrd(links, k8sCrdNamespace, k8sCrdKeep);

    // Close modal
    if (onClose) {
      onClose();
    }
  }

  return (
    <Modal
      title={<div className="mr-4 text-lg">Export To K8s CRD Format</div>}
      width={600}
      open={isOpen}
      onCancel={onClose}
      destroyOnClose
      footer={
        <div className="modal-actions">
          <NGButton variant="secondary" onClick={onClose}>
            Cancel
          </NGButton>
          <NGButton variant="primary" onClick={onOk}>
            Export
          </NGButton>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        {/* Description */}
        <div className="flex flex-col gap-2">
          <div>
            Manage your Control Plane resources as Kubernetes Custom Resource Definitions (CRDs), using the{" "}
            <a
              className="ngfocus color-link font-semibold"
              target="_blank"
              href={`https://github.com/controlplane-com/k8s-operator`}
            >
              Control Plane K8s Operator
            </a>
            .
          </div>
          <div>Adjust the settings below if needed and click on the export button.</div>
        </div>

        {/* Input & Switch */}
        <div className="flex flex-col gap-3">
          {/* Namespace Label & Input */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center">
              <NGLabel>Namespace</NGLabel>
              <InfoTooltip
                title="Specify the Kubernetes namespace for the exported K8s CRD"
                placement="rightTop"
                arrow={{ pointAtCenter: true }}
              />
            </div>
            <NGInput value={k8sCrdNamespace} onChange={(e) => setK8sCrdNamespace(e.target.value)} />
          </div>

          {/* Keep Label & Switch */}
          <div className="flex items-center">
            <NGCheckbox checked={k8sCrdKeep} onChange={setK8sCrdKeep}>
              <span className="pl-1">Prevent deletion of the exported Control Plane K8s CRD</span>
            </NGCheckbox>
            <InfoTooltip
              title="Checking this will add the 'cpln.io/resource-policy: keep' annotation to the exported K8s CRD to prevent it from being deleted by the operator in case this resource is deleted on Kuberentes."
              placement="rightTop"
              arrow={{ pointAtCenter: true }}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export const K8sCrdExportModal = observer(K8sCrdExportModalRaw);
